import { getAgentProjectLandingPath } from "@v2/utils/agentLanding";
import { isOsmanthusEnabled } from "replo-utils/lib/osmanthus";

import "../index.scss";

import * as React from "react";

import { useApplySharedTemplateAfterAuth } from "@/v1/hooks/useApplySharedTemplateAfterAuth";
import { TRPCProviders } from "@app/trpcProviders";
import {
  AppLoadingProvider,
  useAppLoading,
} from "@core/contexts/AppLoadingContext";
import { Analytics as DubAnalytics } from "@dub/analytics/react";
import { CommandMenu } from "@v1/components/CommandMenu";
import SentryBoundary from "@v1/components/common/SentryBoundary";
import { BrandDetailsProvider } from "@v1/components/designLibrary/BrandDetailsContext";
import { ToastManager } from "@v1/components/designSystem/alert/Toast";
import { V1AIStreamingProvider } from "@v1/providers/AIStreamingProvider";
import { store } from "@v1/store/store";
import { getLastSelectedElementIdForProject } from "@v1/utils/localStorage";
import { FullScreenLoader } from "@v2/components/FullScreenLoader";
import { PublicThemeLayout } from "@v2/components/PublicThemeLayout";
import CurrentProjectProvider, {
  useCurrentProjectContext,
  useIsAgentEnabled,
} from "@v2/contexts/CurrentProjectContext";
import { CurrentUserProvider } from "@v2/contexts/CurrentUserContextProvider";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import WorkspaceProvider from "@v2/contexts/WorkspaceProvider";
import { AuthLayout } from "@v2/features/account/AuthLayout";
import GoogleAuthProxy from "@v2/features/account/GoogleAuthProxy";
import InitResetPassword from "@v2/features/account/InitResetPassword";
import LoginForm from "@v2/features/account/LoginForm";
import { captureMarketingWorkflowSteps } from "@v2/features/account/marketingWorkflow";
import { getProjectLandingPath } from "@v2/features/account/postLoginLanding";
import ResetPassord from "@v2/features/account/ResetPasswordForm";
import SignupForm from "@v2/features/account/SignupForm";
import { WorkspaceBillingRedirectRoute } from "@v2/features/billing/WorkspaceBillingRedirectRoute";
import { CurrentChatProvider } from "@v2/features/chat/providers/CurrentChatContext";
import { ChatProvider } from "@v2/features/chat/store/ChatStoreContext";
import DashboardLayout from "@v2/features/dashboard/DashboardLayout";
import BackendStagingBanner from "@v2/features/debug/BackendStagingBanner";
import { GlobalLoginAsChrome } from "@v2/features/debug/LoginAsChrome";
import { useSelectInitialProjectSetupModal } from "@v2/features/onboarding/useSelectInitialProjectSetupModal";
import { NextDevServerProvider } from "@v2/features/page-renderer/components/code-execution/context";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import {
  LocalStorageProvider,
  useLocalStorageState,
} from "@v2/hooks/useLocalStorage";
import { useTheme } from "@v2/hooks/useTheme";
import { EditorStoreProvider } from "@v2/store/editorStore";
import { useModal } from "@v2/store/modalStore";
import { SandboxStateProvider } from "@v2/store/SandboxStateStoreContext";
import { marketingRouter } from "@v2/trpc/routes/marketing";
import { userRouter } from "@v2/trpc/routes/user";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import {
  readFreshMarketingSeedAndPurgeStale,
  storeMarketingSeed,
} from "@v2/utils/marketing-seed";
import { isPublicSharePath, routes } from "@v2/utils/router";
import { CookiesProvider } from "react-cookie";
import { Provider as ReduxProvider } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Outlet,
  redirect,
  redirectDocument,
  Route,
  RouterProvider,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { isAgentApplicationPath } from "replo-utils/lib/agentApplicationPaths";
import { getLandingUserQueryInput } from "replo-utils/lib/lastVisitedDashboardIds";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { Toaster } from "sonner";
import { validate as isUUIDValid } from "uuid";
import { z } from "zod";

import EditorAgentControlRegistration from "./agent-control/EditorAgentControlRegistration";
import AppModals from "./AppModals";
import AuthenticatedUserRequiredRoute from "./AuthenticatedUserRequiredRoute";
import { shouldRedirectUnauthenticatedRootRoute } from "./authRedirect";
import { DebugCommandMenuItemsProvider } from "./DebugCommandMenuItemsProvider";
import { ScopedAnalyticsProvider } from "./ScopedAnalyticsProvider";
import { ShopsRedirectRoute } from "./ShopsRedirectRoute";

// (Ben, 2026-07-06, REPL-28822): Route-level components are lazy-loaded so
// they split out of the entry chunk instead of shipping to every visitor.
const ElementEditorLayout = React.lazy(
  () => import("@/v1/app/ElementEditorLayout"),
);
const TemplateLibraryEditorLayout = React.lazy(
  () => import("@/v1/app/TemplateLibraryEditorLayout"),
);
const Pages = React.lazy(() => import("@/v1/pages/site/Pages"));
const Home = React.lazy(() => import("@v2/features/home/components/Home"));
const AllProjects = React.lazy(
  () => import("@v1/components/dashboard/projects/AllProjects"),
);
const NoAccessToProjectLayout = React.lazy(
  () => import("@v1/components/layouts/errors/NoAccessToProjectLayout"),
);
const ShopifyInstalledOnDifferentProjectLayout = React.lazy(() => {
  return import(
    "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnDifferentProjectLayout"
  );
});
const ShopifyInstalledOnExistingWorkspaceLayout = React.lazy(() => {
  return import(
    "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnExistingWorkspaceLayout"
  );
});
const ShopifyInstalledOnHostingBilledThroughShopifyLayout = React.lazy(() => {
  return import(
    "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnHostingBilledThroughShopifyLayout"
  );
});
const ShopifyProjectHasExistingIntegrationLayout = React.lazy(() => {
  return import(
    "@v1/components/layouts/integrations/shopify/ShopifyProjectHasExistingIntegrationLayout"
  );
});
const ShopifyInstalledOnNoAccessWorkspaceLayout = React.lazy(() => {
  return import(
    "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnNoAccessWorkspaceLayout"
  );
});
const ShopifyPendingAppInstallationLayout = React.lazy(() => {
  return import(
    "@v1/components/layouts/integrations/shopify/ShopifyPendingAppInstallationLayout"
  );
});
const ShareTemplateLayout = React.lazy(
  () => import("@v1/components/layouts/ShareTemplateLayout"),
);
const AnalyticsHome = React.lazy(() => {
  return import("@v1/features/analytics/AnalyticsHome").then((module) => ({
    default: module.AnalyticsHome,
  }));
});
const AnalyticsOverview = React.lazy(() => {
  return import("@v1/features/analytics/AnalyticsOverview").then((module) => ({
    default: module.AnalyticsOverview,
  }));
});
const AnalyticsRedirect = React.lazy(() => {
  return import("@v1/features/analytics/AnalyticsRedirect").then((module) => ({
    default: module.AnalyticsRedirect,
  }));
});
const AnalyticsInsights = React.lazy(
  () => import("@v1/features/analytics/insights/AnalyticsInsights"),
);
const InsightsRedirect = React.lazy(() => {
  return import("@v1/features/analytics/insights/InsightsRedirect").then(
    (module) => ({ default: module.InsightsRedirect }),
  );
});
const PageDetails = React.lazy(
  () => import("@v1/features/analytics/PageDetails"),
);
const ProjectAnalyticsDashboard = React.lazy(() => {
  return import("@v1/features/analytics/WorkspaceAnalyticsDashboard").then(
    (module) => ({ default: module.ProjectAnalyticsDashboard }),
  );
});
const ExperimentEditWrapper = React.lazy(
  () => import("@v1/features/experiments/ExperimentEditWrapper"),
);
const Experiments = React.lazy(
  () => import("@v1/features/experiments/Experiments"),
);
const ExperimentsRedirect = React.lazy(() => {
  return import("@v1/features/experiments/ExperimentsRedirect").then(
    (module) => ({ default: module.ExperimentsRedirect }),
  );
});
const ExperimentDetailsTabV2 = React.lazy(
  () => import("@v1/features/experiments/tabs/ExperimentDetailsTab"),
);
const ExperimentResultsTab = React.lazy(
  () => import("@v1/features/experiments/tabs/ExperimentResultsTab"),
);
const BillingSettingsPage = React.lazy(
  () => import("@v2/features/agent-billing/billing/BillingSettingsPage"),
);
const WorkspaceDashboard = React.lazy(
  () => import("@v2/features/billing/WorkspaceDashboard"),
);
const ReactEditor = React.lazy(
  () => import("@v2/features/chat/components/ReactEditor"),
);
const ChatLayout = React.lazy(
  () => import("@v2/features/chat/layouts/ChatLayout"),
);
const DesignLibrary = React.lazy(() => {
  return import("@v2/features/design-library/DesignLibrary").then((module) => ({
    default: module.DesignLibrary,
  }));
});
const MyReferrals = React.lazy(() => {
  return import("@v2/features/dub/MyReferrals").then((module) => ({
    default: module.MyReferrals,
  }));
});
const HootsuiteContentSourceLayout = React.lazy(() => {
  return import(
    "@v2/features/integrations/hootsuite/HootsuiteContentSourceLayout"
  );
});
const StarterProjectsModal = React.lazy(
  () => import("@v2/features/new-shop-onboarding/StarterProjectsModal"),
);
const WorkspaceWrappedOnboarding = React.lazy(
  () => import("@v2/features/onboarding/WorkspaceWrappedOnboarding"),
);
const ProductEditTab = React.lazy(() => {
  return import("@v2/features/products/ProductEditTab").then((module) => ({
    default: module.ProductEditTab,
  }));
});
const ProductEditWrapper = React.lazy(() => {
  return import("@v2/features/products/ProductEditWrapper").then((module) => ({
    default: module.ProductEditWrapper,
  }));
});
const ProductsDashboard = React.lazy(() => {
  return import("@v2/features/products/ProductsDashboard").then((module) => ({
    default: module.ProductsDashboard,
  }));
});
const Profile = React.lazy(
  () => import("@v2/features/settings/account/Profile"),
);
const SettingsLayout = React.lazy(
  () => import("@v2/features/settings/SettingsLayout"),
);
const AbTestingSettings = React.lazy(
  () => import("@v2/features/settings/shop/AbTesting"),
);
const Advanced = React.lazy(
  () => import("@v2/features/settings/shop/Advanced"),
);
const AnalyticsSettings = React.lazy(
  () => import("@v2/features/settings/shop/Analytics"),
);
const FontManager = React.lazy(
  () => import("@v2/features/settings/shop/FontManager"),
);
const ShopGeneralSettings = React.lazy(
  () => import("@v2/features/settings/shop/General"),
);
const Integrations = React.lazy(
  () => import("@v2/features/settings/shop/Integrations"),
);
const IntegrationsEdit = React.lazy(
  () => import("@v2/features/settings/shop/integrations/IntegrationsEdit"),
);
const IntegrationsHome = React.lazy(
  () => import("@v2/features/settings/shop/integrations/IntegrationsHome"),
);
const ScriptManager = React.lazy(
  () => import("@v2/features/settings/shop/ScriptManager"),
);
const WorkspaceSettings = React.lazy(
  () => import("@v2/features/settings/workspace/General"),
);
const WorkspaceMembers = React.lazy(
  () => import("@v2/features/settings/workspace/Members"),
);
const SharePageTemplateLayout = React.lazy(
  () => import("@v2/features/share-page-template/SharePageTemplateLayout"),
);
const MigrationPage = React.lazy(
  () => import("@v2/features/site-migration/MigrationPage"),
);

/**
 * Paths in onboarding that the user should ONLY see if they have exactly ZERO projects
 * they can access on their account (i.e., they just started and have no projects yet,
 * and have not been invited to one)
 */
const onboardingZeroProjectOnlyPaths = [routes.onboarding.agentSetup];

/**
 * Whether a given url path is part of onboarding
 */
const isOnboardingPath = (pathname: string) => {
  return pathname.startsWith("/onboarding");
};

/**
 * Component which redirects away from specific routes based on the current project type.
 * I.e. if you access /chat on a v1 project we want to redirect them back to the site pages
 * at /site. The /chat should be wrapped in this component with the specific type in this case.
 */
const RedirectBasedOnProjectType: React.FC<
  React.PropsWithChildren<{
    type:
      | "v2Home"
      | "v1SitePages"
      | "v2Chat"
      | "v2DesignLibrary"
      | "v2StyleExtraction";
  }>
> = ({ children, type }) => {
  const { project } = useCurrentProjectContext();
  const isAgentEnabled = useIsAgentEnabled();
  const [searchParams] = useSearchParams();
  const isWelcomeFlow = searchParams.get("welcome") === "true";

  if (!project) {
    return <>{children}</>;
  }

  if (project.hostingType === "replo" && type === "v1SitePages") {
    const homeRoute = isAgentEnabled ? routes.agent.root : routes.shops.home;
    const homePath = generatePath(homeRoute, { projectId: project.id });
    return <Navigate to={homePath} replace />;
  }

  if (
    project.hostingType === "replo" &&
    isAgentEnabled &&
    !isWelcomeFlow &&
    (type === "v2Home" || type === "v2Chat")
  ) {
    return (
      <Navigate
        to={generatePath(routes.agent.root, { projectId: project.id })}
        replace
      />
    );
  }

  if (project.hostingType !== "replo" && ["v2Home", "v2Chat"].includes(type)) {
    // Note (Noah, 2025-09-18): If they're trying to access v2 home or chat but we're a v1 project,
    // we'll redirect them to v1 site pages
    return (
      <Navigate
        to={generatePath(routes.shops.site, { projectId: project.id })}
        replace
      />
    );
  }

  return <>{children}</>;
};

export function App() {
  return (
    <SentryBoundary>
      <ReduxProvider store={store}>
        <TRPCProviders>
          <DubAnalytics
            domainsConfig={{
              refer: "try.replo.app",
            }}
            queryParams={["via", "ref"]}
            cookieOptions={{ domain: ".replo.app" }}
          />
          <EditorAgentControlRegistration />
          <RouterProvider router={router} />
          <ThemedToaster />
        </TRPCProviders>
      </ReduxProvider>
    </SentryBoundary>
  );
}

const router = (() => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RootRoute />}>
        {/* Index redirect route */}
        <Route index element={<RootRouteIndexRedirectRoute />} />
        {/* Google auth proxy for Vercel preview deployments (outside AuthLayout) */}
        <Route path="/auth/google-proxy" element={<GoogleAuthProxy />} />
        <Route element={<PublicThemeLayout />}>
          {/* Auth routes */}
          <Route path="/auth" element={<AuthLayout />}>
            <Route index element={<Navigate to={routes.signup} replace />} />
            <Route path="login" element={<LoginForm />} />
            <Route path="signup" element={<SignupForm />} />
            <Route path="password/reset/init" element={<InitResetPassword />} />
            <Route path="password/reset" element={<ResetPassord />} />
          </Route>
        </Route>
        {/* Retired beta pages still linked from replo.app and sent emails; the root auth gate sends signed-out visitors to signup with their params. */}
        <Route path="/beta/*" element={<Navigate to="/" replace />} />
        {/* NOTE (Yuxin, 2026-08-19): Replo Shops (V2) deprecation — handles its own auth (signup with a return path), so it's excluded from the root auth gate. */}
        <Route path="/migration" element={<MigrationPage />} />
        {/* Public template sharing route */}
        <Route path={routes.share.page} element={<SharePageTemplateLayout />} />
        {/* Redirect routes */}
        <Route
          path="/partner/program"
          element={<PartnerProgramRedirectRoute />}
        />
        <Route
          path="/workspace/billing"
          element={<WorkspaceBillingRedirectRoute />}
        />
        <Route element={<ShopsRedirectRoute />}>
          <Route path={routes.home.root} element={<ShopsRedirectRoute />} />
          <Route
            path={routes.home.shareTemplate}
            element={<ShopsRedirectRoute />}
          />

          {/* Project routes */}
          <Route
            path={routes.allProjects}
            element={
              <AuthenticatedUserRequiredRoute>
                <AllProjects>
                  <AppModals />
                </AllProjects>
              </AuthenticatedUserRequiredRoute>
            }
          />

          {/* Workspace routes */}
          <Route
            path="/workspace/:workspaceId"
            element={
              <AuthenticatedUserRequiredRoute>
                <WorkspaceDashboard />
              </AuthenticatedUserRequiredRoute>
            }
          >
            <Route index element={<WorkspaceIndexRedirectRoute />} />
          </Route>
          <Route
            path="/workspace/:workspaceId/settings"
            element={
              <AuthenticatedUserRequiredRoute>
                <WorkspaceSettingsWrapper />
              </AuthenticatedUserRequiredRoute>
            }
          >
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<WorkspaceSettings />} />
            <Route path="members" element={<WorkspaceMembers />} />
            <Route path="billing" element={<BillingSettingsPage />} />
            <Route path="account">
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<Profile />} />
              <Route path="referrals" element={<MyReferrals />} />
            </Route>
          </Route>
          {/* Analytics workspace agnostic route */}
          <Route path="/analytics" element={<AnalyticsRedirect />} />
          {/* Experiments workspace agnostic route */}
          <Route path="/abtesting" element={<ExperimentsRedirect />} />
          {/* Insights workspace agnostic route */}
          <Route path="/analytics/insights" element={<InsightsRedirect />} />
          {/* Editor routes */}
          <Route path="/editor/:projectId" element={<ProjectRouteWrapper />}>
            <Route index element={<EditorIndexRedirect />} />
            <Route
              path="new"
              element={
                <TemplateLibraryEditorLayout>
                  <AppModals />
                </TemplateLibraryEditorLayout>
              }
            />
            <Route
              path=":elementId"
              element={
                <ElementEditorLayout>
                  <AppModals />
                </ElementEditorLayout>
              }
            />
          </Route>

          {/* Onboarding routes */}
          <Route path="/onboarding">
            <Route
              index
              element={<Navigate to="/onboarding/setup" replace />}
            />
            <Route
              path=":onboardingStep"
              element={
                <AuthenticatedUserRequiredRoute>
                  <WorkspaceWrappedOnboarding>
                    <AppModals />
                  </WorkspaceWrappedOnboarding>
                </AuthenticatedUserRequiredRoute>
              }
            />
          </Route>
        </Route>
        <Route
          path="/template/:templateId"
          element={
            <AuthenticatedUserRequiredRoute>
              <ShareTemplateLayout />
            </AuthenticatedUserRequiredRoute>
          }
        />

        {/* Shops routes */}
        <Route
          path="/shops/:projectId"
          element={
            <AuthenticatedUserRequiredRoute>
              <ToastManager />
              <ProjectRouteWrapper />
            </AuthenticatedUserRequiredRoute>
          }
        >
          <Route index element={<Navigate to="site" replace />} />

          {/* Shops with dashboard layout */}
          <Route element={<DashboardLayout contentIsCard={true} />}>
            {/* /site */}
            <Route
              path="site"
              element={
                <RedirectBasedOnProjectType type="v1SitePages">
                  <Pages />
                </RedirectBasedOnProjectType>
              }
            />

            <Route path="home">
              <Route
                index
                element={
                  <RedirectBasedOnProjectType type="v2Home">
                    <Home />
                  </RedirectBasedOnProjectType>
                }
              />
            </Route>

            {/* /analytics */}
            <Route path="analytics" element={<ProjectAnalyticsDashboard />}>
              <Route path="home" element={<AnalyticsHome />} />
              <Route path="overview" element={<AnalyticsOverview />} />
              <Route path="deep-dive" element={<PageDetails />} />
              <Route path="insights" element={<AnalyticsInsights />} />
            </Route>

            {/* /abtesting */}
            <Route path="abtesting" element={<Experiments />} />
            <Route
              path="abtesting/:experimentId"
              element={<ExperimentEditWrapper />}
            >
              <Route
                path={routes.shops.experiments.details}
                element={<ExperimentDetailsTabV2 />}
              />
              <Route
                path={routes.shops.experiments.results}
                element={<ExperimentResultsTab />}
              />
            </Route>

            {/* /design-library */}
            <Route
              path="design-library"
              element={
                <RedirectBasedOnProjectType type="v2DesignLibrary">
                  <DesignLibrary />
                </RedirectBasedOnProjectType>
              }
            />

            {/* /products */}
            <Route path="products" element={<ProductsDashboard />} />
          </Route>

          {/* /settings */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="shop/general" replace />} />
            {/* /settings/account */}
            <Route path="account">
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<Profile />} />
              <Route path="referrals" element={<MyReferrals />} />
            </Route>
            {/* /settings/shop */}
            <Route path="shop">
              <Route index element={<Navigate to="general" replace />} />
              <Route path="general" element={<ShopGeneralSettings />} />
              <Route path="advanced" element={<Advanced />} />
              <Route path="fonts" element={<FontManager />} />
              <Route path="abtesting" element={<AbTestingSettings />} />
              <Route path="analytics" element={<AnalyticsSettings />} />
              <Route path="scripts" element={<ScriptManager />} />
              <Route path="integrations" element={<Integrations />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<IntegrationsHome />} />
                <Route
                  path=":integrationId/edit"
                  element={<IntegrationsEdit />}
                />
              </Route>
            </Route>
            {/* /settings/workspace */}
            <Route path="workspace">
              <Route index element={<Navigate to="general" replace />} />
              <Route path="general" element={<WorkspaceSettings />} />
              <Route path="members" element={<WorkspaceMembers />} />
              <Route path="billing" element={<BillingSettingsPage />} />
            </Route>
          </Route>

          <Route element={<DashboardLayout contentIsCard={false} />}>
            <Route element={<ChatLayout />}>
              <Route
                path="chat"
                element={
                  <RedirectBasedOnProjectType type="v2Chat">
                    <ReactEditor />
                  </RedirectBasedOnProjectType>
                }
              />
            </Route>
            <Route element={<ProductEditWrapper />}>
              {
                // NOTE (Kurt, 2025-09-01): We put this products edit route in the
                // contentIsCard = false layout because we want the product edits screen
                // to have two seperate cards instead of a unified one.
              }
              <Route
                path="products/:productId/edit"
                element={<ProductEditTab />}
              />
              <Route path="products/new" element={<ProductEditTab />} />
            </Route>
          </Route>
        </Route>

        {/* Error routes */}
        <Route path="error">
          <Route path="no-access" element={<NoAccessToProjectLayout />} />
        </Route>

        {/* Integrations routes */}
        <Route path="shopify">
          <Route
            path="install"
            element={<ShopifyPendingAppInstallationLayout />}
          />
          <Route
            path="install/existing-integration"
            element={<ShopifyProjectHasExistingIntegrationLayout />}
          />
          <Route path="error">
            <Route
              path="different-project"
              element={<ShopifyInstalledOnDifferentProjectLayout />}
            />
            <Route
              path="different-workspace"
              element={<ShopifyInstalledOnExistingWorkspaceLayout />}
            />
            <Route
              path="no-access-workspace"
              element={<ShopifyInstalledOnNoAccessWorkspaceLayout />}
            />
            <Route
              path="hosting-billed-through-shopify"
              element={<ShopifyInstalledOnHostingBilledThroughShopifyLayout />}
            />
          </Route>
        </Route>

        {/* External integrations routes */}
        <Route path="external-integrations">
          <Route path="hootsuite" element={<HootsuiteContentSourceLayout />} />
        </Route>
        <Route
          path="*"
          loader={({ request }) => {
            const { pathname } = new URL(request.url);
            return isAgentApplicationPath(pathname)
              ? redirectDocument(request.url)
              : redirect(routes.home.root);
          }}
        />
      </Route>,
    ),
  );
})();

function RootRoute() {
  return (
    <RootRouteProviders>
      <RootRouteLayout>
        <Outlet />
      </RootRouteLayout>
    </RootRouteProviders>
  );
}

function subscribeToRouter(onStoreChange: () => void) {
  return router.subscribe(onStoreChange);
}

function getRouterPathname() {
  return router.state.location.pathname;
}

function ThemedToaster() {
  const pathname = React.useSyncExternalStore(
    subscribeToRouter,
    getRouterPathname,
  );
  const { effectiveMode, cssVars } = useTheme();
  const isPublicThemedRoute =
    pathname === "/auth" || pathname.startsWith("/auth/");

  const defaultToasterTheme = isOsmanthusEnabled() ? "system" : "light";

  return (
    <Toaster
      theme={isPublicThemedRoute ? effectiveMode : defaultToasterTheme}
      style={isPublicThemedRoute ? cssVars : undefined}
    />
  );
}

function RootRouteProviders({ children }: React.PropsWithChildren) {
  return (
    // NOTE (Martin, 2024-10-09): We need an extra SentryBoundary component
    // here so that Sentry can catch errors inside the routing tree before
    // the React Router Boundary does it.
    <SentryBoundary>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
        <LocalStorageProvider>
          <CurrentUserProvider>
            <V1AIStreamingProvider>
              <BrandDetailsProvider>
                <ChatProvider>
                  <AppLoadingProvider>{children}</AppLoadingProvider>
                </ChatProvider>
              </BrandDetailsProvider>
            </V1AIStreamingProvider>
          </CurrentUserProvider>
        </LocalStorageProvider>
      </CookiesProvider>
    </SentryBoundary>
  );
}

/**
 * Loads Google Tag Manager on all dashboard pages in production.
 *
 * @author Ben Vogt 2025-05-02
 */
function useGoogleTagManager() {
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    const scriptHTML = `
      <!-- Google Tag Manager -->
      <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-MH56PQ8');</script>
      <!-- End Google Tag Manager -->
    `;

    const tempElement = document.createElement("div");
    tempElement.innerHTML = scriptHTML.trim();
    if (tempElement.firstChild) {
      document.head.append(tempElement.firstChild);
    }
  }, []);
}

function PylonChatHider() {
  const { pathname } = useLocation();
  const isOnboarding = pathname.includes("/onboarding");

  if (!isOnboarding && !isPublicSharePath(pathname)) {
    return null;
  }

  return <style>{`.PylonChat { display: none !important; }`}</style>;
}

function RootRouteLayout({ children }: React.PropsWithChildren) {
  const { closeModalsWithExclusions } = useModal();
  const [isUsingBackendStaging] = useLocalStorageState(
    "replo.debug.useBackendStaging",
    false,
    { schema: z.boolean() },
  );
  const shouldShowBackendStagingBanner = isUsingBackendStaging;

  const { pathname, search } = useLocation();

  // Track last non-settings page for "Back to app" navigation
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (!pathname.includes("/settings")) {
      sessionStorage.setItem("lastNonSettingsPath", `${pathname}${search}`);
    }
  }, [pathname, search]);

  const { user, isLoading: isLoadingUser, isAuthenticated } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const hash = window.location.hash;
  captureMarketingWorkflowSteps({ hash, searchParams });
  const seedToken = new URLSearchParams(hash.slice(1)).get("seed");

  const { isLoading: isLoadingMarketingSeed } =
    marketingRouter.getMarketingWebsiteSeedData.useQuery(
      { seed: seedToken! },
      {
        enabled: Boolean(seedToken),
        select: (seedData) => {
          if (seedData) {
            storeMarketingSeed({
              prompt: seedData.prompt,
              jti: seedData.jti,
              hasImage: Boolean(seedData.imageUrl),
            });
            window.history.replaceState(
              null,
              "",
              window.location.pathname + window.location.search,
            );
          }
          return seedData;
        },
      },
    );

  useOnValueChange(pathname, () => {
    closeModalsWithExclusions({
      typesToExclude: [
        "deleteSwatchConfirmationModal",
        "deleteElementConfirmationModal",
      ],
    });
  });

  useGoogleTagManager();
  const isBareRootRoute = pathname === "/" && search === "" && hash === "";
  useAppLoading(
    "user",
    !isBareRootRoute && (isLoadingUser || isLoadingMarketingSeed),
  );

  if (isLoadingUser || isLoadingMarketingSeed) {
    return null;
  }

  // NOTE (Martin, 2024-10-03): if we are not in auth routes and the user is not
  // authenticated, we redirect to the signup page.
  // Note (Ezra, 2025-11-11): Share pages are publicly accessible without
  // requiring authentication.
  // Note (Noah, 2026-02-20): We exclude /external-integrations/ from the auth redirect
  // because external integration pages (e.g., Hootsuite content source) should be
  // publicly accessible without requiring authentication.
  if (
    shouldRedirectUnauthenticatedRootRoute({
      isAuthenticated,
      isLoadingUser,
      isPublicPath: isPublicSharePath(pathname),
      pathname,
    })
  ) {
    const allParams = searchParams.toString();
    const redirectUrl = `${routes.signup}${allParams ? `?${allParams}` : ""}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // NOTE (Ben O. 2025-04-28): If the user hasn't completed onboarding, we redirect to the onboarding page.
  // this doesn't apply if we are redirecting after a Shopify integration from onboarding. useConnectShopifyIntegration
  // handles the redirection for that.

  // NOTE (Berkley, 2025-09-10): This is the state check for whether the user has completed onboarding
  // First with check if the user is associated with a project or workspace with a project. Basically, is there a project they can go to?
  // If so, we do not redirect, and let them continue to their route
  // If not, we check if the user has a first and last name. If not, we direct them to user setup step
  // If they do, we direct them to the project setup step.
  const userHasAccessToAnyProject = Boolean(user?.defaultProjectId);
  const doesUserHaveName = user?.firstName || user?.lastName;
  const isAgentFunnel =
    user?.workspace &&
    "defaultProjectType" in user.workspace &&
    user.workspace.defaultProjectType === "agent";

  // Note (Noah, 2025-09-26): If the user has access to any project and this happens to be a
  // route that we only want them to see if they have no projects, then redirect them to home
  // which will redirect them to their first project.
  const shouldDirectUserToProject =
    isAuthenticated &&
    !isLoadingUser &&
    onboardingZeroProjectOnlyPaths.some((path) => pathname.startsWith(path)) &&
    userHasAccessToAnyProject;
  if (shouldDirectUserToProject) {
    // (Ben, 2026-07-08, REPL-28833): Send agent users to their Launchpad, not
    // the Shopify `/shops` surface — `/shops` redirects rebuild the URL from
    // projectId alone, dropping the launchpad tail and landing them on Home.
    if (isAgentFunnel && user?.defaultProjectId) {
      const homePath = getAgentProjectLandingPath({
        projectId: user.defaultProjectId,
      });
      return <Navigate to={homePath} replace />;
    }
    return <Navigate to={routes.shops.default} replace />;
  }

  // Note (Noah, 2025-09-26): If we're not in an onboarding route, but the user has zero projects,
  // when we need to redirect them back to onboarding. This is e.g. if the user somehow accesses
  // dashboard.replo.app/home after dropping out of the onboarding flow. We redirect to a different
  // part of the flow depending on what info they have filled out on their user object
  const shouldDirectUserToOnboarding =
    isAuthenticated &&
    !isLoadingUser &&
    !isOnboardingPath(pathname) &&
    !isPublicSharePath(pathname) &&
    (!doesUserHaveName || !userHasAccessToAnyProject);

  if (shouldDirectUserToOnboarding) {
    const getOnboardingPathToRedirectTo = () => {
      if (isAgentFunnel && !userHasAccessToAnyProject) {
        return routes.onboarding.agentSetup;
      }
      return routes.onboarding.userInfo;
    };

    const redirectUrl = `${getOnboardingPathToRedirectTo()}?${searchParams.toString()}`;

    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <>
      {shouldShowBackendStagingBanner && <BackendStagingBanner />}
      <PylonChatHider />
      {isAuthenticated && <CommandMenu />}
      <DebugCommandMenuItemsProvider />
      <GlobalLoginAsChrome />
      <React.Suspense fallback={<FullScreenLoader />}>
        {children}
      </React.Suspense>
    </>
  );
}

function RootRouteIndexRedirectRoute() {
  const templateId = useTemplateIdFromSearchParams();
  // Fresh last-visited read + staleTime:0 so "/" matches LoginForm landing
  // (REPL-30512 / REPL-30166).
  const [landingUserQueryInput] = React.useState(getLandingUserQueryInput);
  const { data: landingUser, isLoading: isLoadingLandingUser } =
    userRouter.get.useQuery(landingUserQueryInput, {
      staleTime: 0,
      enabled: !templateId,
    });
  const projectLandingPath = templateId
    ? null
    : getProjectLandingPath(landingUser);
  const { data: workspaceData, isLoading: isLoadingWorkspaces } =
    workspaceRouter.getUserWorkspacesList.useQuery(undefined, {
      enabled:
        !templateId && !isLoadingLandingUser && projectLandingPath === null,
    });

  if (!templateId && isLoadingLandingUser) {
    return null;
  }

  if (projectLandingPath !== null) {
    return <Navigate to={projectLandingPath} replace />;
  }

  if (isLoadingWorkspaces) {
    return null;
  }

  if (templateId) {
    return (
      <Navigate
        to={generatePath(routes.home.shareTemplate, { templateId })}
        replace
      />
    );
  }

  const userHasOnlyOneWorkspace =
    (workspaceData?.workspaces ?? []).length === 1;
  const toUrl =
    userHasOnlyOneWorkspace && workspaceData?.workspaces![0]!.id
      ? generatePath("/workspace/:workspaceId/projects", {
          workspaceId: workspaceData.workspaces[0].id,
        })
      : routes.home.root;

  return <Navigate to={toUrl} replace />;
}

function PartnerProgramRedirectRoute() {
  const { user } = useCurrentUser();

  return (
    <Navigate
      to={
        user!.workspace?.id
          ? generatePath("/workspace/:workspaceId/partner/program", {
              workspaceId: user!.workspace.id,
            })
          : routes.home.root
      }
      replace
    />
  );
}

function WorkspaceSettingsWrapper() {
  const { workspaceId } = useParams();

  if (!workspaceId || !isUUIDValid(workspaceId ?? "")) {
    return <Navigate to={routes.home.root} />;
  }

  return (
    <WorkspaceProvider
      workspaceId={workspaceId}
      saveWorkspaceIdAsLastVisited
      showToastOnNoAccess
      redirectOnNoAccess
    >
      <SettingsLayout />
    </WorkspaceProvider>
  );
}

function WorkspaceIndexRedirectRoute() {
  const workspaceId = useCurrentWorkspaceId();
  return (
    <Navigate
      to={
        workspaceId
          ? generatePath(routes.workspace.projects, {
              workspaceId,
            })
          : routes.home.root
      }
      replace
    />
  );
}

function useTemplateIdFromSearchParams() {
  const [searchParams] = useSearchParams();
  return searchParams.get("templateId");
}

function ProjectRouteWrapper() {
  const { projectId } = useParams<{ projectId: string }>();

  if (!projectId) {
    return <Navigate to={routes.home.root} replace />;
  }

  return (
    <CurrentProjectProvider projectId={projectId} saveProjectIdAsLastVisited>
      <ProjectContentWithRedirect projectId={projectId} />
    </CurrentProjectProvider>
  );
}

/**
 * Redirect component for /editor/:projectId (no elementId).
 * Picks the last-selected element from localStorage, or the first element,
 * and redirects to /editor/:projectId/:elementId.
 */
function EditorIndexRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { elementMetadata, isElementMetadataSuccess } =
    useCurrentProjectContext();

  if (!projectId || !isElementMetadataSuccess || !elementMetadata) {
    return null;
  }

  const lastElementId = getLastSelectedElementIdForProject(projectId);
  const validLastElement =
    lastElementId &&
    elementMetadata.some((element) => {
      return element.id === lastElementId && element.projectId === projectId;
    });
  const targetElementId = validLastElement
    ? lastElementId
    : elementMetadata[0]?.id;

  const searchString = searchParams.toString();
  const suffix = searchString ? `?${searchString}` : "";

  if (!targetElementId) {
    return <Navigate to={`/editor/${projectId}/new${suffix}`} replace />;
  }

  return (
    <Navigate to={`/editor/${projectId}/${targetElementId}${suffix}`} replace />
  );
}

/**
 * Component that handles the project content and marketing seed redirects
 */
function ProjectContentWithRedirect({ projectId }: { projectId: string }) {
  const { project, isLoading } = useCurrentProjectContext();
  const { pathname } = useLocation();

  // Note (Ezra, 2025-11-11): Apply shared template after auth - moved here from RootRouteLayout
  // to match marketing seed pattern. Only runs when on a project route, so onboarding checks
  // are not needed.
  useApplySharedTemplateAfterAuth();

  if (isLoading) {
    return null;
  }

  const marketingSeedData = readFreshMarketingSeedAndPurgeStale();

  if (
    marketingSeedData &&
    project?.hostingType === "replo" &&
    !pathname.includes("/chat") &&
    project.styleLibrary
  ) {
    return <Navigate to={`/shops/${projectId}/chat`} replace />;
  }

  return (
    <ScopedAnalyticsProvider>
      {/* TODO (Gabe 2025-06-18): This thing will rerender on log output which is not great. */}
      <EditorStoreProvider>
        <SandboxStateProvider>
          <NextDevServerProvider>
            <CurrentChatProvider>
              <InitialProjectSetupModal projectId={projectId} />
              <Outlet />
            </CurrentChatProvider>
          </NextDevServerProvider>
        </SandboxStateProvider>
      </EditorStoreProvider>
    </ScopedAnalyticsProvider>
  );
}

/**
 * This modal is used to check for full project onboarding status and show modals as needed
 */
function InitialProjectSetupModal({ projectId }: { projectId: string }) {
  const { modalToShow } = useSelectInitialProjectSetupModal();

  if (modalToShow === null) {
    return null;
  }

  return exhaustiveSwitch({ type: modalToShow })({
    starterSites: <StarterProjectsModal projectId={projectId} />,
  });
}
